import { Component, input } from '@angular/core';
import { ObjectType } from '../../../tokens-and-interfaces';
import {
  Association,
  CrmFieldService,
  CrmObjectDisplayService,
  CrmPipelineService,
  StandardIds,
  SystemFieldIds,
} from '../../../shared-services';
import { CrmObjectAssociationPanelComponent } from '../crm-object-association-panel/crm-object-association-panel.component';
import { OpportunityStatus, PAGE_ROUTES } from '../../../constants';
import { AssociationCardData } from '../crm-object-association-panel/tokens-and-interfaces';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { AsyncPipe, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { EMPTY, map } from 'rxjs';

export interface OpportunityAssociationCardData extends AssociationCardData {
  value?: number;
  currency?: string;
  lastActivity?: Date;
  expectedClose?: Date;
  status?: string;
}

@Component({
  selector: 'crm-opportunity-association-panel',
  templateUrl: './opportunity-association-panel.component.html',
  styleUrls: ['../../../shared-styles/association-panel-shared.scss', 'opportunity-association-panel.component.scss'],
  imports: [
    CrmObjectAssociationPanelComponent,
    GalaxyPipesModule,
    DatePipe,
    TranslateModule,
    GalaxyBadgeModule,
    AsyncPipe,
  ],
})
export class OpportunityAssociationPanelComponent {
  id = input('');
  name = input('');
  objectType = input<ObjectType>('Opportunity');

  protected readonly ClosedWon = OpportunityStatus.ClosedWon;
  protected readonly ClosedLost = OpportunityStatus.ClosedLost;
  protected readonly Open = OpportunityStatus.Open;

  protected readonly cardFunction = (associations: Association[], routePrefix: string): AssociationCardData[] => {
    return associations.map((association) => {
      const fullName = this.opportunityCustomizationService.getObjectDisplayName(
        'Opportunity',
        association.associatedObject || {},
      );

      const currencyValue = this.fieldService.getFieldValueFromCrmObject(
        association.associatedObject,
        StandardIds.OpportunityAmount,
      );
      const lastActivity = this.fieldService.getFieldValueFromCrmObject(
        association.associatedObject,
        SystemFieldIds.OpportunityLastActivityDate,
      );
      const closeDate = this.fieldService.getFieldValueFromCrmObject(
        association.associatedObject,
        StandardIds.OpportunityExpectedCloseDate,
      );
      const status = this.fieldService.getFieldValueFromCrmObject(
        association.associatedObject,
        StandardIds.OpportunityStatus,
      );
      const stageField = this.fieldService.getFieldValueFromCrmObject(
        association.associatedObject,
        StandardIds.OpportunityCalculatedStageID,
      );
      const pipelineField = this.fieldService.getFieldValueFromCrmObject(
        association.associatedObject,
        StandardIds.OpportunityPipelineID,
      );

      const pipelineId = pipelineField?.stringValue ?? '';
      const stageId = stageField?.stringValue ?? '';

      const pipeline$ = pipelineId && stageId ? this.crmPipelineService.getPipeline$(pipelineId) : EMPTY;
      const stageName$ = pipeline$.pipe(
        map((pipeline) => pipeline?.stages || []),
        map((stages) => stages.find((stage) => stage.id === stageId)),
        map((stage) => stage?.name || ''),
      );

      return {
        associationId: association.associationId,
        objectId: association.associatedObject.crmObjectId,
        name: fullName,
        profileUrl:
          `${routePrefix}/${PAGE_ROUTES.OPPORTUNITY.ROOT}/${PAGE_ROUTES.OPPORTUNITY.SUBROUTES.PROFILE}`.replace(
            ':crmObjectId',
            association.associatedObject.crmObjectId || '',
          ),
        tags: association.tags || [],
        value: currencyValue?.currencyValue?.value ?? 0,
        currency: currencyValue?.currencyValue?.currencyCode ?? 'USD',
        lastActivity: lastActivity?.dateValue,
        expectedClose: closeDate?.dateValue,
        status: status?.stringValue ?? 'Open',
        stageName$: stageName$,
      } as OpportunityAssociationCardData;
    });
  };

  constructor(
    private readonly opportunityCustomizationService: CrmObjectDisplayService,
    private readonly fieldService: CrmFieldService,
    private readonly crmPipelineService: CrmPipelineService,
  ) {}
}
